import React, { useState } from 'react';
import { campaignsAPI } from '../../../../services/api';
import './Story.css';

function Story({ campaign, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('backstory');

  const [backstory, setBackstory] = useState(campaign?.backstory || '');
  const [timeline, setTimeline] = useState(() => {
    try {
      return campaign?.timeline ? JSON.parse(campaign.timeline) : [];
    } catch {
      return [];
    }
  });
  const [plotThreads, setPlotThreads] = useState(() => {
    try {
      return campaign?.plotThreads ? JSON.parse(campaign.plotThreads) : [];
    } catch {
      return [];
    }
  });
  const [decisionTrees, setDecisionTrees] = useState(() => {
    try {
      return campaign?.decisionTrees ? JSON.parse(campaign.decisionTrees) : [];
    } catch {
      return [];
    }
  });

  // Timeline management
  const [newEvent, setNewEvent] = useState({ date: '', title: '', description: '' });

  const addTimelineEvent = () => {
    if (!newEvent.title) return;
    const event = {
      id: Date.now(),
      date: newEvent.date,
      title: newEvent.title,
      description: newEvent.description,
      createdAt: new Date().toISOString()
    };
    setTimeline([...timeline, event]);
    setNewEvent({ date: '', title: '', description: '' });
  };

  const deleteTimelineEvent = (id) => {
    setTimeline(timeline.filter(e => e.id !== id));
  };

  // Plot Thread management
  const [newThread, setNewThread] = useState({ title: '', description: '', status: 'Planned' });

  const addPlotThread = () => {
    if (!newThread.title) return;
    const thread = {
      id: Date.now(),
      title: newThread.title,
      description: newThread.description,
      status: newThread.status,
      createdAt: new Date().toISOString()
    };
    setPlotThreads([...plotThreads, thread]);
    setNewThread({ title: '', description: '', status: 'Planned' });
  };

  const updatePlotThreadStatus = (id, newStatus) => {
    setPlotThreads(plotThreads.map(thread => 
      thread.id === id ? { ...thread, status: newStatus } : thread
    ));
  };

  const deletePlotThread = (id) => {
    setPlotThreads(plotThreads.filter(t => t.id !== id));
  };

  // Decision Tree management
  const [newDecision, setNewDecision] = useState({ 
    scenario: '', 
    options: [{ text: '', consequence: '' }] 
  });

  const addDecisionTree = () => {
    if (!newDecision.scenario) return;
    const tree = {
      id: Date.now(),
      scenario: newDecision.scenario,
      options: newDecision.options.filter(o => o.text),
      createdAt: new Date().toISOString()
    };
    setDecisionTrees([...decisionTrees, tree]);
    setNewDecision({ scenario: '', options: [{ text: '', consequence: '' }] });
  };

  const addDecisionOption = () => {
    setNewDecision({
      ...newDecision,
      options: [...newDecision.options, { text: '', consequence: '' }]
    });
  };

  const updateDecisionOption = (index, field, value) => {
    const updatedOptions = [...newDecision.options];
    updatedOptions[index][field] = value;
    setNewDecision({ ...newDecision, options: updatedOptions });
  };

  const deleteDecisionTree = (id) => {
    setDecisionTrees(decisionTrees.filter(t => t.id !== id));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);

      const payload = {
        backstory,
        timeline: JSON.stringify(timeline),
        plotThreads: JSON.stringify(plotThreads),
        decisionTrees: JSON.stringify(decisionTrees)
      };

      const res = await campaignsAPI.updateCampaign(campaign.id, payload);
      const updated = res.data?.campaign;
      
      if (updated) {
        onUpdate(updated);
        setIsEditing(false);
      }
    } catch (e) {
      console.error('Failed to save story:', e);
      setError(e?.response?.data?.error || 'Failed to save story');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setBackstory(campaign?.backstory || '');
    try {
      setTimeline(campaign?.timeline ? JSON.parse(campaign.timeline) : []);
      setPlotThreads(campaign?.plotThreads ? JSON.parse(campaign.plotThreads) : []);
      setDecisionTrees(campaign?.decisionTrees ? JSON.parse(campaign.decisionTrees) : []);
    } catch {
      // Reset to empty if parsing fails
    }
    setIsEditing(false);
    setError(null);
  };

  const renderBackstory = () => (
    <div className="story-content-section">
      <h3>Campaign Backstory</h3>
      {isEditing ? (
        <textarea
          value={backstory}
          onChange={(e) => setBackstory(e.target.value)}
          disabled={isSaving}
          rows={15}
          placeholder="Write the backstory for your campaign world, events leading up to the start, important historical context..."
        />
      ) : (
        <div className="display-value multiline">
          {backstory || 'No backstory written yet'}
        </div>
      )}
    </div>
  );

  const renderTimeline = () => (
    <div className="story-content-section">
      <h3>Campaign Timeline</h3>
      
      {isEditing && (
        <div className="timeline-add-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="Date (e.g., Session 3, Year 312)"
              value={newEvent.date}
              onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
            />
            <input
              type="text"
              placeholder="Event title"
              value={newEvent.title}
              onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
            />
          </div>
          <textarea
            placeholder="Event description..."
            value={newEvent.description}
            onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
            rows={2}
          />
          <button className="btn-secondary" onClick={addTimelineEvent}>
            Add Event
          </button>
        </div>
      )}

      <div className="timeline-list">
        {timeline.length === 0 ? (
          <div className="empty-state">No timeline events yet</div>
        ) : (
          timeline.map(event => (
            <div key={event.id} className="timeline-event">
              <div className="timeline-event-header">
                <span className="timeline-date">{event.date}</span>
                <span className="timeline-title">{event.title}</span>
                {isEditing && (
                  <button 
                    className="btn-delete"
                    onClick={() => deleteTimelineEvent(event.id)}
                  >
                    ×
                  </button>
                )}
              </div>
              {event.description && (
                <div className="timeline-description">{event.description}</div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderPlotThreads = () => (
    <div className="story-content-section">
      <h3>Plot Threads</h3>
      
      {isEditing && (
        <div className="plot-thread-add-form">
          <input
            type="text"
            placeholder="Plot thread title"
            value={newThread.title}
            onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
          />
          <textarea
            placeholder="Plot thread description..."
            value={newThread.description}
            onChange={(e) => setNewThread({ ...newThread, description: e.target.value })}
            rows={2}
          />
          <select
            value={newThread.status}
            onChange={(e) => setNewThread({ ...newThread, status: e.target.value })}
          >
            <option value="Planned">Planned</option>
            <option value="Active">Active</option>
            <option value="Resolved">Resolved</option>
            <option value="Abandoned">Abandoned</option>
          </select>
          <button className="btn-secondary" onClick={addPlotThread}>
            Add Plot Thread
          </button>
        </div>
      )}

      <div className="plot-threads-grid">
        {['Planned', 'Active', 'Resolved', 'Abandoned'].map(status => (
          <div key={status} className="plot-thread-column">
            <h4 className={`status-${status.toLowerCase()}`}>{status}</h4>
            {plotThreads
              .filter(thread => thread.status === status)
              .map(thread => (
                <div key={thread.id} className="plot-thread-card">
                  <div className="plot-thread-header">
                    <strong>{thread.title}</strong>
                    {isEditing && (
                      <button 
                        className="btn-delete"
                        onClick={() => deletePlotThread(thread.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {thread.description && (
                    <p className="plot-thread-description">{thread.description}</p>
                  )}
                  {isEditing && (
                    <select
                      value={thread.status}
                      onChange={(e) => updatePlotThreadStatus(thread.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="Planned">Planned</option>
                      <option value="Active">Active</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Abandoned">Abandoned</option>
                    </select>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );

  const renderDecisionTrees = () => (
    <div className="story-content-section">
      <h3>Decision Trees</h3>
      
      {isEditing && (
        <div className="decision-tree-add-form">
          <input
            type="text"
            placeholder="Scenario / Decision Point"
            value={newDecision.scenario}
            onChange={(e) => setNewDecision({ ...newDecision, scenario: e.target.value })}
          />
          
          <div className="decision-options">
            <h4>Options:</h4>
            {newDecision.options.map((option, index) => (
              <div key={index} className="decision-option-row">
                <input
                  type="text"
                  placeholder={`Option ${index + 1}`}
                  value={option.text}
                  onChange={(e) => updateDecisionOption(index, 'text', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Consequence"
                  value={option.consequence}
                  onChange={(e) => updateDecisionOption(index, 'consequence', e.target.value)}
                />
              </div>
            ))}
            <button className="btn-secondary" onClick={addDecisionOption}>
              + Add Option
            </button>
          </div>

          <button className="btn-secondary" onClick={addDecisionTree}>
            Add Decision Tree
          </button>
        </div>
      )}

      <div className="decision-trees-list">
        {decisionTrees.length === 0 ? (
          <div className="empty-state">No decision trees yet</div>
        ) : (
          decisionTrees.map(tree => (
            <div key={tree.id} className="decision-tree-card">
              <div className="decision-tree-header">
                <strong>{tree.scenario}</strong>
                {isEditing && (
                  <button 
                    className="btn-delete"
                    onClick={() => deleteDecisionTree(tree.id)}
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="decision-options-list">
                {tree.options.map((option, index) => (
                  <div key={index} className="decision-option">
                    <span className="option-text">→ {option.text}</span>
                    {option.consequence && (
                      <span className="option-consequence">⇒ {option.consequence}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="story-section">
      <div className="section-header">
        <h2>Campaign Story</h2>
        <div className="section-actions">
          {!isEditing ? (
            <button className="btn-primary" onClick={() => setIsEditing(true)}>
              Edit Story
            </button>
          ) : (
            <>
              <button className="btn-secondary" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="story-tabs">
        <button 
          className={`story-tab ${activeTab === 'backstory' ? 'active' : ''}`}
          onClick={() => setActiveTab('backstory')}
        >
          Backstory
        </button>
        <button 
          className={`story-tab ${activeTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          Timeline
        </button>
        <button 
          className={`story-tab ${activeTab === 'plotThreads' ? 'active' : ''}`}
          onClick={() => setActiveTab('plotThreads')}
        >
          Plot Threads
        </button>
        <button 
          className={`story-tab ${activeTab === 'decisionTrees' ? 'active' : ''}`}
          onClick={() => setActiveTab('decisionTrees')}
        >
          Decision Trees
        </button>
      </div>

      <div className="story-content">
        {activeTab === 'backstory' && renderBackstory()}
        {activeTab === 'timeline' && renderTimeline()}
        {activeTab === 'plotThreads' && renderPlotThreads()}
        {activeTab === 'decisionTrees' && renderDecisionTrees()}
      </div>
    </div>
  );
}

export default Story;